import {
    Grid,
    InputAdornment,
    TextField,
    TextFieldProps,
    Theme,
    Typography,
} from '@mui/material'
import PlusAndMinusButton from 'components/PlusAndMinusButton/PlusAndMinusButton'
import { isNil } from 'lodash-es'
import { useCallback, useState } from 'react'
import { useController, UseControllerProps } from 'react-hook-form'
import NumberFormat, { NumberFormatValues } from 'react-number-format'
import useReactHookFormError from './useReactHookFormError'
import { useScreenSize } from 'hooks/useScreenSize/useScreenSize'
import { FilledTextField } from './FilledTextField/FilledTextField'

function OWNumberInput<T extends object>({
    name,
    control,
    fullWidth,
    isAllowed,
    sx,
    label,
    prefix,
    disabled,
    suffix,
    manuallyOnChange,
    onChangeCallback,
    isPlusOrMinusButton = false,
    placeholder,
    allowLeadingZeros = true,
    fixedSuffixPosition = false,
    fixedPrefixPosition = false,
    autoSelect = false,
    readOnly = false,
    decimalScale = 2,
    // force all input to be negative number
    isNegativeNumber = false,
}: TextFieldProps &
    UseControllerProps<T> & {
        isAllowed?: ((values: NumberFormatValues) => boolean) | undefined
        suffix?: string
        manuallyOnChange?: (val: number) => void
        isPlusOrMinusButton?: boolean
        onChangeCallback?: (value: number) => void
        allowLeadingZeros?: boolean
        fixedSuffixPosition?: boolean
        fixedPrefixPosition?: boolean
        autoSelect?: boolean
        readOnly?: boolean
        decimalScale?: number
        isNegativeNumber?: boolean
    }) {
    const {
        field,
        formState: { errors },
    } = useController<T>({ name, control })
    const { isMobile } = useScreenSize()

    const Component = isMobile ? FilledTextField : TextField

    const { value, onChange, ref } = field

    // discard `type` property, to prevent unintentionally overridding TextField's prop
    const { type: _, ...error } = useReactHookFormError(errors, name)

    const [isPlus, setIsPlus] = useState<boolean>(true)

    const getAdjustPlusOrMinusValue = useCallback(
        (val: number) => {
            if (isPlus) return Math.abs(val)
            else return Math.abs(val) * -1
        },
        [isPlus],
    )
    const onUpdateValue = useCallback(
        (val: any) => {
            if (isNegativeNumber) val = -1 * val
            if (manuallyOnChange) manuallyOnChange(val)
            else onChange(val)
            if (onChangeCallback) {
                onChangeCallback(val)
            }
        },
        [manuallyOnChange, onChange, onChangeCallback, isNegativeNumber],
    )
    const handlePlus = useCallback(() => {
        setIsPlus(true)
        let val = Math.abs(value)
        onUpdateValue(val)
    }, [onUpdateValue, value])

    const handleMinus = useCallback(() => {
        setIsPlus(false)
        let val = Math.abs(value) * -1
        onUpdateValue(val)
    }, [value, onUpdateValue])

    return (
        <Grid item container spacing={1}>
            {isPlusOrMinusButton && (
                <Grid item>
                    <Grid item container>
                        <PlusAndMinusButton
                            onPlus={handlePlus}
                            onMinus={handleMinus}
                        />
                    </Grid>
                </Grid>
            )}
            <Grid item xs>
                <NumberFormat
                    allowLeadingZeros={allowLeadingZeros}
                    placeholder={placeholder}
                    disabled={disabled}
                    fullWidth={fullWidth}
                    customInput={Component}
                    thousandSeparator={true}
                    getInputRef={ref}
                    label={label}
                    isAllowed={isAllowed}
                    value={isNil(value) ? '' : Math.abs(value)}
                    sx={{
                        input: {
                            color: (theme: Theme) =>
                                isNil(value) || parseFloat(value) >= 0
                                    ? theme.palette.text.primary
                                    : theme.palette.error.main,
                        },
                        ...sx,
                    }}
                    prefix={fixedPrefixPosition ? undefined : prefix}
                    suffix={fixedSuffixPosition ? undefined : suffix}
                    decimalScale={decimalScale}
                    onValueChange={(v) => {
                        if (!v.floatValue && v.floatValue !== 0) {
                            onUpdateValue(null)
                        } else {
                            let val = v.floatValue
                            if (isPlusOrMinusButton)
                                val = getAdjustPlusOrMinusValue(val)
                            onUpdateValue(val)
                        }
                    }}
                    onFocus={(e) => {
                        if (
                            (typeof value === 'number' &&
                                Number(value) === 0) ||
                            autoSelect
                        )
                            e.target.select()
                    }}
                    {...error}
                    InputProps={{
                        startAdornment:
                            fixedPrefixPosition && prefix ? (
                                <InputAdornment position="start">
                                    <Typography component="span">
                                        {prefix}
                                    </Typography>
                                </InputAdornment>
                            ) : undefined,
                        endAdornment:
                            fixedSuffixPosition && suffix ? (
                                <InputAdornment position="end">
                                    <Typography component="span">
                                        {suffix}
                                    </Typography>
                                </InputAdornment>
                            ) : undefined,
                        readOnly: readOnly,
                    }}
                />
            </Grid>
        </Grid>
    )
}

export default OWNumberInput
